import { XIcon, CopyIcon, EnvelopeIcon, ChatTeardropTextIcon, ShareNetworkIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import { brandUrl } from "@/lib/api";

/**
 * Live-platform-style share sheet: a bottom-anchored modal with platform tiles.
 *
 * Each tile composes a deep-link or sharer URL and either opens it in a new
 * tab (web platforms) or fires the OS share sheet via navigator.share(). The
 * native share button at the bottom is the universal fallback / catch-all.
 *
 * All outbound links are run through `brandUrl(...)` so a preview pod or
 * native WebView never leaks `*.preview.emergentagent.com` into a tweet,
 * SMS, or email. Shared URLs always read as bidorah.com.
 */
export default function ShareSheet({ open, onClose, url, title, sellerName }) {
  if (!open) return null;

  const shareUrl = brandUrl(url);
  const shareTitle = title || "Bidorah Live";
  const shareText = `Watch ${sellerName || "this seller"} live on Bidorah`;

  const platforms = [
    {
      id: "copy",
      label: "Copy link",
      bg: "bg-zinc-200 text-zinc-900",
      icon: <CopyIcon size={22} weight="fill" />,
      onClick: () => {
        navigator.clipboard?.writeText(shareUrl);
        toast.success("Link copied");
        onClose();
      },
    },
    {
      id: "whatsapp",
      label: "WhatsApp",
      bg: "bg-[#25D366] text-white",
      icon: <span className="font-black text-lg">W</span>,
      href: `https://wa.me/?text=${encodeURIComponent(shareText + " " + shareUrl)}`,
    },
    {
      id: "telegram",
      label: "Telegram",
      bg: "bg-[#229ED9] text-white",
      icon: <span className="font-black text-lg">T</span>,
      href: `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`,
    },
    {
      id: "twitter",
      label: "X / Twitter",
      bg: "bg-black text-white",
      icon: <span className="font-black text-lg">𝕏</span>,
      href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
    },
    {
      id: "facebook",
      label: "Facebook",
      bg: "bg-[#1877F2] text-white",
      icon: <span className="font-black text-lg">f</span>,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    },
    {
      id: "messenger",
      label: "Messenger",
      bg: "bg-gradient-to-br from-[#00B2FF] to-[#006AFF] text-white",
      icon: <ChatTeardropTextIcon size={22} weight="fill" />,
      href: `https://www.facebook.com/dialog/send?app_id=&link=${encodeURIComponent(shareUrl)}&redirect_uri=${encodeURIComponent(shareUrl)}`,
    },
    {
      id: "reddit",
      label: "Reddit",
      bg: "bg-[#FF4500] text-white",
      icon: <span className="font-black text-base">R</span>,
      href: `https://www.reddit.com/submit?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareTitle)}`,
    },
    {
      id: "linkedin",
      label: "LinkedIn",
      bg: "bg-[#0A66C2] text-white",
      icon: <span className="font-black text-base">in</span>,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
    },
    {
      id: "sms",
      label: "Text / SMS",
      bg: "bg-emerald-500 text-white",
      icon: <ChatTeardropTextIcon size={22} weight="fill" />,
      href: `sms:?&body=${encodeURIComponent(shareText + " " + shareUrl)}`,
    },
    {
      id: "email",
      label: "Email",
      bg: "bg-zinc-700 text-white",
      icon: <EnvelopeIcon size={22} weight="fill" />,
      href: `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(shareText + "\n\n" + shareUrl)}`,
    },
    {
      id: "native",
      label: "More",
      bg: "bg-gradient-to-br from-zinc-800 to-black text-white",
      icon: <ShareNetworkIcon size={22} weight="fill" />,
      onClick: async () => {
        if (navigator.share) {
          try {
            await navigator.share({ title: shareTitle, text: shareText, url: shareUrl });
          } catch { /* user dismissed */ }
        } else {
          navigator.clipboard?.writeText(shareUrl);
          toast.success("Link copied");
        }
        onClose();
      },
    },
  ];

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center"
      onClick={onClose}
      data-testid="share-sheet"
    >
      <div
        className="bg-white w-full sm:max-w-md sm:mx-4 sm:rounded-3xl rounded-t-3xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        style={{ paddingBottom: "max(env(safe-area-inset-bottom, 0px), 96px)" }}
      >
        <div className="px-5 pt-4 pb-3 flex items-center justify-between border-b border-zinc-100">
          <div>
            <div className="font-display font-black text-lg leading-tight">Share this live</div>
            <div className="text-xs text-zinc-500 mt-0.5">
              {sellerName ? `Send ${sellerName}'s show to friends` : "Send this show to friends"}
            </div>
          </div>
          <button
            onClick={onClose}
            className="h-9 w-9 rounded-full bg-zinc-100 grid place-items-center hover:bg-zinc-200"
            data-testid="share-sheet-close"
          >
            <XIcon size={18} weight="bold" />
          </button>
        </div>
        <div className="grid grid-cols-4 gap-3 p-5 max-h-[60vh] overflow-y-auto">
          {platforms.map((p) => {
            const tile = (
              <div className="flex flex-col items-center gap-1.5">
                <span className={`h-12 w-12 rounded-2xl grid place-items-center ${p.bg} shadow-sm active:scale-95 transition-transform`}>
                  {p.icon}
                </span>
                <span className="text-[11px] font-semibold text-zinc-700 text-center leading-tight">
                  {p.label}
                </span>
              </div>
            );
            const testid = `share-${p.id}`;
            return p.href ? (
              <a
                key={p.id}
                href={p.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onClose}
                data-testid={testid}
              >
                {tile}
              </a>
            ) : (
              <button
                key={p.id}
                type="button"
                onClick={p.onClick}
                data-testid={testid}
              >
                {tile}
              </button>
            );
          })}
        </div>
        <div className="px-5 pb-3">
          <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-400 mb-1.5">
            Link
          </div>
          <div className="flex gap-2 items-center bg-zinc-100 rounded-xl px-3 py-2.5">
            <span className="flex-1 text-[12px] text-zinc-700 font-mono truncate">
              {shareUrl}
            </span>
            <button
              onClick={() => {
                navigator.clipboard?.writeText(shareUrl);
                toast.success("Link copied");
              }}
              className="text-[11px] font-bold text-[#1D78AB] uppercase tracking-wider"
              data-testid="share-sheet-copy-link"
            >
              Copy
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
